import { useEffect } from "react";

// React Aria's modal overlay marks elements outside the modal with `inert` to
// trap focus. The OneTrust cookie consent container lives outside the modal, so
// it gets `inert` too — making the consent buttons unclickable (SST-9055).
// This hook removes `inert` from the OneTrust container while the error modal
// is open, reacting to DOM changes via MutationObserver instead of polling.

const ONETRUST_CONTAINER_ID = "onetrust-consent-sdk";
// The consent-dialog backdrop. It is display:none only when the consent UI is
// hidden; the outer #onetrust-consent-sdk wrapper is always display:block (it
// also holds the floating "Manage Cookies" button), so it is not a usable
// visibility signal.
const ONETRUST_DARK_FILTER_SELECTOR = ".onetrust-pc-dark-filter";

// How long to wait for React Aria to add `inert` after the modal opens.
const CONTAINER_INERT_WAIT_MS = 1000;
// How long to wait for OneTrust itself to be inserted into the DOM.
const ONETRUST_INSERT_WAIT_MS = 10000;

// Consent is "handled" (banner won't block anything) when the user has already
// accepted/rejected, OR the consent UI is rendered but hidden.
function isConsentHandled(): boolean {
  if (globalThis.window === undefined) return false;

  if (globalThis.window.OneTrust?.IsAlertBoxClosed?.() === true) return true;

  const darkFilter = document.querySelector(ONETRUST_DARK_FILTER_SELECTOR);
  return (
    !!darkFilter && globalThis.getComputedStyle(darkFilter).display === "none"
  );
}

// Remove `inert` from the container if present. Returns true when it removed it.
function removeInert(container: Element | null): boolean {
  if (container?.hasAttribute("inert")) {
    container.removeAttribute("inert");
    return true;
  }
  return false;
}

export function useOnetrustInertRemoval(isOpenModal: boolean): void {
  useEffect(() => {
    if (!isOpenModal || typeof document === "undefined") return;

    // Skip entirely when consent is already handled — nothing can be blocked.
    if (isConsentHandled()) return;

    let containerObserver: MutationObserver | null = null;
    let bodyObserver: MutationObserver | null = null;
    let containerTimer: ReturnType<typeof setTimeout> | undefined;
    let bodyTimer: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      containerObserver?.disconnect();
      bodyObserver?.disconnect();
      clearTimeout(containerTimer);
      clearTimeout(bodyTimer);
    };

    // Watch a present container for `inert` being added, then remove it.
    const watchContainerForInert = (container: Element) => {
      // Already inert? Remove now and we're done.
      if (removeInert(container)) {
        cleanup();
        return;
      }

      containerObserver = new MutationObserver(() => {
        if (removeInert(container)) {
          cleanup();
        }
      });
      containerObserver.observe(container, {
        attributes: true,
        attributeFilter: ["inert"],
      });

      containerTimer = setTimeout(() => {
        containerObserver?.disconnect();
      }, CONTAINER_INERT_WAIT_MS);
    };

    const existingContainer = document.getElementById(ONETRUST_CONTAINER_ID);
    if (existingContainer) {
      watchContainerForInert(existingContainer);
    } else {
      // OneTrust not in the DOM yet — watch for its insertion. OneTrust appends
      // #onetrust-consent-sdk as a direct child of <body>, so observing direct
      // children (childList) is enough; subtree would fire on every descendant
      // mutation (constant in a React app) for no benefit.
      bodyObserver = new MutationObserver(() => {
        const container = document.getElementById(ONETRUST_CONTAINER_ID);
        if (!container) return;

        bodyObserver?.disconnect();
        clearTimeout(bodyTimer);

        // It may have rendered already-handled; re-check before waiting.
        if (isConsentHandled()) {
          cleanup();
          return;
        }
        watchContainerForInert(container);
      });
      bodyObserver.observe(document.body, {
        childList: true,
      });

      bodyTimer = setTimeout(() => {
        bodyObserver?.disconnect();
      }, ONETRUST_INSERT_WAIT_MS);
    }

    return cleanup;
  }, [isOpenModal]);
}
